// Location Risk Profile Component (Phase 4)
// Displays comprehensive location risk profile with breakdown

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, TrendingUp, TrendingDown, Minus, RefreshCw, Calendar, MapPin } from 'lucide-react';
import { useLocationRiskProfile } from '../../hooks/useLocationRisk';
import Modal from '../ui/Modal';
import './LocationRiskProfile.css';

const FACTOR_LABEL_KEYS = {
  historical: 'factorHistorical',
  patient: 'factorPatient',
  environmental: 'factorEnvironmental',
  staffing: 'factorStaffing',
  incidents: 'factorIncidents',
  devices: 'factorDevices'
};

const LocationRiskProfile = ({ locationId, locationType = 'room', onClose = null, embedded = false }) => {
  const { t } = useTranslation('patientRisk');
  const [showRefresh, setShowRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  const { profile, loading, error, refresh } = useLocationRiskProfile(locationId, locationType);

  const getFactorLabel = (key) => t(FACTOR_LABEL_KEYS[key] || key);
  const getRiskLevelLabel = (level) => {
    if (!level) return '';
    const l = level.toLowerCase();
    if (l === 'critical') return t('riskLevelCritical');
    if (l === 'high') return t('riskLevelHighLabel');
    if (l === 'medium') return t('riskLevelMediumLabel');
    if (l === 'low') return t('riskLevelLowLabel');
    return level;
  };

  const handleRefresh = async () => {
    setShowRefresh(true);
    try {
      await refresh();
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error refreshing profile:', error);
    } finally {
      setShowRefresh(false);
    }
  };

  if (loading && !profile) {
    return (
      <div className="location-risk-profile loading">
        <div className="spinner"></div>
        <p>{t('calculatingProfile')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="location-risk-profile error">
        <AlertTriangle size={24} color="#ef4444" />
        <p>{t('errorLoadingProfile')} {error}</p>
        <button className="btn btn-secondary" onClick={handleRefresh}>
          {t('retry')}
        </button>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="location-risk-profile empty">
        <p>{t('noRiskProfileAvailable')}</p>
        <button className="btn btn-primary" onClick={handleRefresh}>
          {t('calculateRiskProfile')}
        </button>
      </div>
    );
  }

  const riskFactors = profile.risk_factors || {};
  // Get multipliers from profile (saved) or reconstruct from risk_factors
  const multipliers = profile.multipliers || {
    historical: riskFactors.historical_infection_rate?.multiplier || 1.0,
    patient: riskFactors.current_patient_risk?.multiplier || 1.0,
    environmental: riskFactors.environmental_conditions?.multiplier || 1.0,
    staffing: riskFactors.staffing_adequacy?.multiplier || 1.0,
    incidents: riskFactors.recent_incidents?.multiplier || 1.0,
    devices: riskFactors.device_risk?.multiplier || 1.0
  };
  const riskLevel = profile.risk_level || 'Low';
  const riskLevelColors = {
    Critical: '#dc3545',
    High: '#fd7e14',
    Medium: '#ffc107',
    Low: '#28a745'
  };

  const getTrendIcon = (trend) => {
    if (trend === 'increasing') return <TrendingUp size={16} color="#ef4444" />;
    if (trend === 'decreasing') return <TrendingDown size={16} color="#10b981" />;
    return <Minus size={16} color="#6b7280" />;
  };

  const getMultiplierBar = (multiplier, max = 3.0) => {
    const percentage = Math.min((multiplier / max) * 100, 100);
    let color = '#28a745'; // Low
    if (multiplier >= 2.0) color = '#dc3545'; // Critical
    else if (multiplier >= 1.5) color = '#fd7e14'; // High
    else if (multiplier >= 1.2) color = '#ffc107'; // Medium
    
    return (
      <div className="multiplier-bar-container">
        <div 
          className="multiplier-bar-fill" 
          style={{ 
            width: `${percentage}%`, 
            backgroundColor: color 
          }}
        />
        <span className="multiplier-value">{multiplier.toFixed(2)}×</span>
      </div>
    );
  };

  const content = (
    <div className="location-risk-profile">
      {/* Header */}
      <div className="risk-profile-header">
        <div>
          <h3>
            <MapPin size={20} /> {profile.location_name || t('locationPlaceholder', { id: locationId })}
          </h3>
          <p className="location-type">{locationType === 'department' ? t('departmentRiskProfile') : locationType === 'room' ? t('roomRiskProfile') : t('locationRiskProfile')}</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={handleRefresh} disabled={showRefresh}>
            <RefreshCw size={16} /> {showRefresh ? t('calculating') : t('recalculate')}
          </button>
          {onClose && (
            <button className="btn btn-secondary" onClick={onClose}>
              {t('close')}
            </button>
          )}
        </div>
      </div>

      {/* Overall Risk Summary */}
      <div className="risk-summary-card">
        <div className="risk-level-display">
          <div 
            className="risk-level-badge" 
            style={{ backgroundColor: riskLevelColors[riskLevel] || '#6b7280' }}
          >
            <AlertTriangle size={24} /> {getRiskLevelLabel(riskLevel)}
          </div>
          <div className="risk-multiplier-display">
            <div className="multiplier-label">{t('locationRiskMultiplier')}</div>
            <div className="multiplier-value-large">{profile.location_risk_multiplier?.toFixed(2) || '1.00'}×</div>
          </div>
        </div>
        <p className="risk-rationale">{profile.risk_level_rationale || t('riskAssessmentComplete')}</p>
        {profile.last_calculated && (
          <div className="last-updated">
            <Calendar size={14} />
            {t('lastCalculatedLabel')} {profile.last_calculated?.toDate?.()?.toLocaleString() || 'Recently'}
          </div>
        )}
      </div>

      {/* Calculation Summary */}
      <div className="calculation-summary">
        <h4>{t('calculationSummary')}</h4>
        <div className="calculation-steps">
          <div className="calculation-step">
            <span className="step-label">{t('baseMultiplier')}</span>
            <span className="step-value">1.0×</span>
          </div>
          {Object.entries(multipliers || {}).map(([factor, value]) => (
            <div key={factor} className="calculation-step">
              <span className="step-operator">×</span>
              <span className="step-label">{getFactorLabel(factor)}:</span>
              <span className="step-value">{value?.toFixed(2) || '1.00'}×</span>
            </div>
          ))}
          <div className="calculation-step calculation-result">
            <span className="step-operator">=</span>
            <span className="step-label">{t('rawMultiplier')}</span>
            <span className="step-value">
              {(() => {
                const raw = Object.values(multipliers).reduce((a, b) => a * b, 1.0);
                return raw.toFixed(2);
              })()}×
            </span>
          </div>
          {(() => {
            const raw = Object.values(multipliers).reduce((a, b) => a * b, 1.0);
            return raw > 3.0;
          })() && (
            <div className="calculation-step calculation-capped">
              <span className="step-operator">→</span>
              <span className="step-label">{t('cappedAt30')}</span>
              <span className="step-value">3.00×</span>
            </div>
          )}
        </div>
      </div>

      {/* Risk Factor Breakdown */}
      <div className="risk-factors-section">
        <h4>{t('riskFactorDetails')}</h4>
        
        {/* Historical Infections */}
        <div className="risk-factor-item">
          <div className="factor-header">
            <span className="factor-name">{t('historicalInfections')}</span>
            <span className="factor-multiplier">{multipliers.historical?.toFixed(2) || '1.00'}×</span>
          </div>
          {getMultiplierBar(multipliers.historical || 1.0)}
          <div className="factor-details">
            {riskFactors.historical_infection_rate && (
              <>
                <div className="factor-value-row">
                  <span className="value-label">{t('infectionRate')}</span>
                  <span className="value-value">{riskFactors.historical_infection_rate.rate_per_1000_patient_days?.toFixed(1) || '5.0'} {t('per1000PatientDays')}</span>
                  <span className="value-note">{t('nationalBenchmark')}</span>
                </div>
                {riskFactors.historical_infection_rate.trend && (
                  <div className="factor-value-row">
                    <span className="value-label">{t('trend')}</span>
                    <span className="trend-indicator">
                      {getTrendIcon(riskFactors.historical_infection_rate.trend)}
                      {riskFactors.historical_infection_rate.trend}
                    </span>
                  </div>
                )}
                {riskFactors.historical_infection_rate.cases_last_30_days !== undefined && (
                  <div className="factor-value-row">
                    <span className="value-label">{t('cases30d')}</span>
                    <span className="value-value">{riskFactors.historical_infection_rate.cases_last_30_days || 0}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Current Patients */}
        <div className="risk-factor-item">
          <div className="factor-header">
            <span className="factor-name">{t('currentPatients')}</span>
            <span className="factor-multiplier">{multipliers.patient?.toFixed(2) || '1.00'}×</span>
          </div>
          {getMultiplierBar(multipliers.patient || 1.0)}
          <div className="factor-details">
            {riskFactors.current_patient_risk && (
              <>
                <div className="factor-value-row">
                  <span className="value-label">{t('totalPatients')}</span>
                  <span className="value-value">{riskFactors.current_patient_risk.total_patients || 0}</span>
                </div>
                {riskFactors.current_patient_risk.high_risk_patients > 0 && (
                  <div className="factor-value-row">
                    <span className="value-label">{t('highRiskLabel')}</span>
                    <span className="high-risk-indicator">{riskFactors.current_patient_risk.high_risk_patients}</span>
                  </div>
                )}
                {riskFactors.current_patient_risk.immunocompromised_count > 0 && (
                  <div className="factor-value-row">
                    <span className="value-label">{t('immunocompromised')}</span>
                    <span className="value-value">{riskFactors.current_patient_risk.immunocompromised_count}</span>
                  </div>
                )}
                {riskFactors.current_patient_risk.isolation_count > 0 && (
                  <div className="factor-value-row">
                    <span className="value-label">{t('isolationLabel')}</span>
                    <span className="value-value">{riskFactors.current_patient_risk.isolation_count}</span>
                  </div>
                )}
                <div className="factor-value-row">
                  <span className="value-label">{t('averageRiskMultiplier')}</span>
                  <span className="value-value">{riskFactors.current_patient_risk.average_risk_multiplier?.toFixed(2) || '1.00'}×</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Environmental Conditions */}
        <div className="risk-factor-item">
          <div className="factor-header">
            <span className="factor-name">{t('environmentalConditions')}</span>
            <span className="factor-multiplier">{multipliers.environmental?.toFixed(2) || '1.00'}×</span>
          </div>
          {getMultiplierBar(multipliers.environmental || 1.0)}
          <div className="factor-details">
            {riskFactors.environmental_conditions && (
              <>
                <div className="factor-value-row">
                  <span className="value-label">{t('ventilation')}</span>
                  <span className="value-value">{riskFactors.environmental_conditions.ventilation_status || 'adequate'}</span>
                </div>
                <div className="factor-value-row">
                  <span className="value-label">{t('cleaningCompliance')}</span>
                  <span className="value-value">{riskFactors.environmental_conditions.cleaning_compliance || 95}%</span>
                  <span className="value-note">{t('target95')}</span>
                </div>
                <div className="factor-value-row">
                  <span className="value-label">{t('humidity')}</span>
                  <span className="value-value">{riskFactors.environmental_conditions.humidity_level || 50}%</span>
                  <span className="value-note">{t('ideal4060')}</span>
                </div>
                <div className="factor-value-row">
                  <span className="value-label">{t('temperature')}</span>
                  <span className="value-value">{riskFactors.environmental_conditions.temperature_level || 22}°C</span>
                  <span className="value-note">{t('ideal2024')}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Staffing Adequacy */}
        <div className="risk-factor-item">
          <div className="factor-header">
            <span className="factor-name">{t('staffingAdequacy')}</span>
            <span className="factor-multiplier">{multipliers.staffing?.toFixed(2) || '1.00'}×</span>
          </div>
          {getMultiplierBar(multipliers.staffing || 1.0)}
          <div className="factor-details">
            {riskFactors.staffing_adequacy && (
              <>
                <div className="factor-value-row">
                  <span className="value-label">{t('currentStaff')}</span>
                  <span className="value-value">{riskFactors.staffing_adequacy.current_staff_count || 0}</span>
                </div>
                <div className="factor-value-row">
                  <span className="value-label">{t('requiredStaff')}</span>
                  <span className="value-value">{riskFactors.staffing_adequacy.required_staff_count || 8}</span>
                </div>
                <div className="factor-value-row">
                  <span className="value-label">{t('staffingRatio')}</span>
                  <span className={`ratio-indicator ${(riskFactors.staffing_adequacy.ratio || 0) < 0.8 ? 'low' : (riskFactors.staffing_adequacy.ratio || 0) >= 1.0 ? 'adequate' : ''}`}>
                    {(riskFactors.staffing_adequacy.ratio || 0).toFixed(2)}
                    {riskFactors.staffing_adequacy.ratio < 0.8 ? ` ${t('understaffed')}` : riskFactors.staffing_adequacy.ratio >= 1.0 ? ` ${t('adequate')}` : ''}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Recent Incidents */}
        <div className="risk-factor-item">
          <div className="factor-header">
            <span className="factor-name">{t('recentIncidents')}</span>
            <span className="factor-multiplier">{multipliers.incidents?.toFixed(2) || '1.00'}×</span>
          </div>
          {getMultiplierBar(multipliers.incidents || 1.0)}
          <div className="factor-details">
            {riskFactors.recent_incidents && (
              <>
                <div className="factor-value-row">
                  <span className="value-label">{t('haiCases')}</span>
                  <span className="value-value">{riskFactors.recent_incidents.hai_cases || 0}</span>
                </div>
                <div className="factor-value-row">
                  <span className="value-label">{t('nearMisses')}</span>
                  <span className="value-value">{riskFactors.recent_incidents.near_misses || 0}</span>
                </div>
                <div className="factor-value-row">
                  <span className="value-label">{t('complianceFailures')}</span>
                  <span className="value-value">{riskFactors.recent_incidents.compliance_failures || 0}</span>
                </div>
                <div className="factor-value-row">
                  <span className="value-label">Total Incidents:</span>
                  <span className="value-value">
                    {(riskFactors.recent_incidents.hai_cases || 0) + 
                     (riskFactors.recent_incidents.near_misses || 0) + 
                     (riskFactors.recent_incidents.compliance_failures || 0)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Device Density */}
        <div className="risk-factor-item">
          <div className="factor-header">
            <span className="factor-name">Device Density</span>
            <span className="factor-multiplier">{multipliers.devices?.toFixed(2) || '1.00'}×</span>
          </div>
          {getMultiplierBar(multipliers.devices || 1.0)}
          <div className="factor-details">
            {riskFactors.device_risk && (
              <>
                <span>
                  {riskFactors.device_risk.invasive_devices_per_bed?.toFixed(1) || '0.0'} invasive devices per bed
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Endemic Organisms */}
      {profile.endemic_organisms && profile.endemic_organisms.length > 0 && (
        <div className="endemic-organisms-section">
          <h4>Endemic Organisms</h4>
          <div className="organisms-list">
            {profile.endemic_organisms.map((org, idx) => (
              <div key={idx} className="organism-item">
                <span className="organism-name">{org.entity_name || org.name}</span>
                <span className="organism-prevalence">{org.prevalence || 'unknown'}</span>
                {org.last_detected && (
                  <span className="organism-date">
                    Last detected: {org.last_detected.toDate?.()?.toLocaleDateString() || 'Unknown'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {profile.recommendations && profile.recommendations.length > 0 && (
        <div className="recommendations-section">
          <h4>Recommended Precautions</h4>
          <ul className="recommendations-list">
            {profile.recommendations.map((rec, idx) => (
              <li key={idx}>{rec}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  // If embedded, return content directly; otherwise wrap in modal
  if (embedded) {
    return content;
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={t('locationRiskProfile')}
      size="large"
    >
      {content}
    </Modal>
  );
};

export default LocationRiskProfile;
